import { G } from '@2l/utils'

import { apiClient } from '@/plugins/apiClient'

const isAuthenticated = G('defaults.headers.common.X-Session-Id')

class BaseProxy {
  /**
   * The constructor of the BaseProxy.
   *
   * @param {string} endpoint   The endpoint being used.
   * @param {Object} parameters The parameters for the request.
   */
  constructor(endpoint, parameters = {}) {
    if (typeof endpoint === 'object') {
      parameters = endpoint
      endpoint = null
    }
    this.$http = apiClient
    this._endpoint = endpoint || 'api/v12'
    this.parameters = parameters
  }

  /**
   * Returns this._endpoint. It uses to override in subclasses
   */
  get endpoint() {
    return this._endpoint
  }

  /**
   * Returns true if session is exists
   *
   * @return {Boolean}
   */
  isAuthenticated() {
    return !!isAuthenticated(this.$http)
  }

  /**
   * Method used to set the query parameters.
   *
   * @param {Object} parameters The given parameters.
   *
   * @returns {BaseProxy} The instance of the proxy.
   */
  setParameters(parameters) {
    Object.keys(parameters).forEach((key) => {
      this.parameters[key] = parameters[key]
    })

    return this
  }

  /**
   * Method used to set a single parameter.
   *
   * @param {string} parameter The given parameter.
   * @param {*} value The value to be set.
   *
   * @returns {BaseProxy} The instance of the proxy.
   */
  setParameter(parameter, value) {
    this.parameters[parameter] = value

    return this
  }

  /**
   * Method used to remove all the parameters.
   *
   * @param {Array} parameters The given parameters.
   *
   * @returns {BaseProxy} The instance of the proxy.
   */
  removeParameters(parameters) {
    parameters.forEach((parameter) => {
      delete this.parameters[parameter]
    })

    return this
  }

  /**
   * Method used to remove a single parameter.
   *
   * @param {string} parameter The given parameter.
   *
   * @returns {BaseProxy} The instance of the proxy.
   */
  removeParameter(parameter) {
    delete this.parameters[parameter]

    return this
  }

  /**
   * The method used to perform an AJAX-request.
   *
   * @param {string}      requestType The request type.
   * @param {string}      url         The URL for the request.
   * @param {Object|null} data        The data to be send with the request.
   *
   * @returns {Promise} The result in a promise.
   */
  async submit(requestType, url, data = null) {
    try {
      const payload = {
        searchParams: {
          ...this.getParameters(),
          ...(data?.params || {}),
          ...(data?.searchParams || {})
        }
      }

      if (Object.keys(payload.searchParams).length === 0) {
        delete payload.searchParams
      }

      if (requestType !== 'get' && data) {
        if (data.headers) {
          const { headers, ...rest } = data
          payload.headers = headers
          payload.json = rest
        } else {
          payload.json = data
        }
      }

      const response = await this.$http[requestType](url, payload)
      return await response.json()
    } catch (error) {
      if (error.response) {
        const errorResponse = await error.response.json()
        throw errorResponse
      }
      throw error
    }
  }

  /**
   * Method used to fetch all items from the API.
   *
   * @param {Object} params - query parameters
   *
   * @returns {Promise} The result in a promise.
   */
  all(searchParams = {}) {
    return this.submit('get', `${this.endpoint}`, { searchParams })
  }

  /**
   * Method used to fetch a single item from the API.
   *
   * @param {Int} id - The given identifier.
   * @param {Object} params - query parameters
   *
   * @returns {Promise} The result in a promise.
   */
  find(id, searchParams = {}) {
    return this.submit('get', `${this.endpoint}/${id}`, { searchParams })
  }

  validate(params = {}) {
    return this.submit('post', 'sync/v1/validate', params)
  }

  /**
   * Method used to create an item.
   *
   * @param {Object} item The given item.
   *
   * @returns {Promise} The result in a promise.
   */
  create(item) {
    return this.submit('post', `${this.endpoint}`, item)
  }

  /**
   * Method used to update an item.
   *
   * @param {int}    id   The given identifier.
   * @param {Object} item The given item.
   *
   * @returns {Promise} The result in a promise.
   */
  update(id, item) {
    return this.submit('put', `${this.endpoint}/${id}`, item)
  }

  /**
   * Method used to destroy an item.
   *
   * @param {int} id The given identifier.
   *
   * @returns {Promise} The result in a promise.
   */
  destroy(id) {
    return this.submit('delete', `${this.endpoint}/${id}`)
  }

  /**
   * Method used to transform a parameters object to a filtered parameters object.
   *
   * @returns {object} The parameter object.
   */
  getParameters() {
    return Object.keys(this.parameters).reduce((acc, key) => {
      if (this.parameters[key]) {
        acc[key] = this.parameters[key]
      }
      return acc
    }, {})
  }

  /**
   * Method used to transform a parameters object to a parameters string.
   *
   * @returns {string} The parameter string.
   */
  getParameterString() {
    const keys = Object.keys(this.parameters)

    const parameterStrings = keys
      .filter((key) => !!this.parameters[key])
      .map((key) => `${key}=${this.parameters[key]}`)

    return parameterStrings.length === 0 ? '' : `?${parameterStrings.join('&')}`
  }

  /**
   * lookup uses to detect real url for static resource.
   * use it only for non-production environment
   * */
  async lookup(url, method = 'HEAD') {
    if (!url) {
      return url
    }

    const result = await fetch(url, { method })
    if (!result.ok || result.status === 404) {
      return url.replace('dev.appewa.com', 'appewa.com')
    }

    return url
  }
}

export default BaseProxy
